import apiClient from "./apiClient";
import { API_ENDPOINTS } from "../config/config";

/**
 * API Key Service — Settings → API Keys.
 *
 * These calls use the raw axios client instead of `apiRequest`, because the
 * component needs the HTTP status:
 *   - GET 404/405 means the backend predates the list endpoint (legacy mode);
 *   - 409 on create may carry a structured `conflict` beside the readable detail;
 *   - 429 carries a Retry-After header.
 *
 * Every function resolves (never rejects) to
 *   { success: true,  status, data }
 *   { success: false, status, message, conflict, retryAfter }
 * where `message` is always a plain string, safe to render.
 */

// Minting does a Secret Manager round trip, so mutations get more time than
// the client-wide 10 s default.
export const API_KEY_MUTATION_TIMEOUT_MS = 30000;

const FALLBACK_BY_STATUS = {
  400: "The request was not valid.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "The API key was not found.",
  405: "This server does not support that action.",
  409: "That conflicts with the current state of your API keys.",
  422: "The request was not valid.",
  429: "Too many requests. Please wait and try again."
};

const fieldFromLoc = (loc) => {
  if (!Array.isArray(loc)) return "";
  return loc
    .filter((part) => !["body", "query", "path"].includes(part))
    .map(String)
    .join(".");
};

/** Turn a FastAPI `detail` (string, validation array or object) into text. */
export const detailToText = (detail) => {
  if (typeof detail === "string") return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item.msg === "string") {
          const field = fieldFromLoc(item.loc);
          return field ? `${field}: ${item.msg}` : item.msg;
        }
        return "";
      })
      .filter(Boolean)
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
  }
  return "";
};

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    const value = headers.get(name);
    if (value !== undefined && value !== null) return value;
  }
  return headers[name] ?? headers[name.toLowerCase()];
};

/** Retry-After as whole seconds (delta-seconds or HTTP-date), or null. */
export const parseRetryAfter = (value, now = Date.now()) => {
  if (value === undefined || value === null || value === "") return null;
  const text = String(value).trim();
  if (/^\d+$/.test(text)) return parseInt(text, 10);
  const at = Date.parse(text);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - now) / 1000));
};

const describeWait = (seconds) => {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
};

const isTimeout = (error) =>
  error?.code === "ECONNABORTED" ||
  error?.code === "ETIMEDOUT" ||
  /timeout/i.test(error?.message || "");

/** Normalise an axios error into the failure shape described above. */
export const toFailure = (error, fallback, { mutation = false } = {}) => {
  const response = error?.response;

  if (!response) {
    let message;
    if (isTimeout(error)) {
      message = mutation
        ? "The server took too long to respond. The change may still have been applied — refresh the key list before trying again."
        : "The server took too long to respond. Please try again.";
    } else if (error?.request) {
      message = "Network error. Please check your connection and try again.";
    } else {
      message = fallback;
    }
    return {
      success: false,
      status: null,
      message,
      conflict: null,
      retryAfter: null
    };
  }

  const status = response.status;
  const data = response.data;
  let message = detailToText(data?.detail);
  if (!message && typeof data?.message === "string") message = data.message;
  if (!message) {
    message =
      FALLBACK_BY_STATUS[status] ||
      (status >= 500
        ? `Server error (HTTP ${status}). Please try again later.`
        : fallback);
  }

  let retryAfter = null;
  if (status === 429) {
    retryAfter = parseRetryAfter(readHeader(response.headers, "retry-after"));
    // The server's detail usually says when to retry already; only add the
    // wait when it doesn't, so the sentence is never doubled or contradicted.
    if (retryAfter !== null && !/try again/i.test(message)) {
      message = `${message} Try again in ${describeWait(retryAfter)}.`;
    }
  }

  const conflict =
    status === 409 &&
    data?.conflict &&
    typeof data.conflict === "object" &&
    data.conflict.key_id
      ? data.conflict
      : null;

  return { success: false, status, message, conflict, retryAfter };
};

const send = async (call, fallback, options) => {
  try {
    const response = await call();
    return { success: true, status: response.status, data: response.data };
  } catch (error) {
    return toFailure(error, fallback, options);
  }
};

/**
 * List the organization's API keys.
 * Calls: GET /api/v1/api-keys?include_inactive=true|false
 * 200 → { mint_policy, can_manage, limits, items[] }. 404/405 → legacy backend.
 */
export const listApiKeys = ({ includeInactive = false } = {}) =>
  send(
    () =>
      apiClient.get(API_ENDPOINTS.API_KEYS, {
        params: { include_inactive: includeInactive }
      }),
    "Failed to load API keys"
  );

/**
 * Create (or rotate) an API key.
 * Calls: POST /api/v1/api-keys
 *   additive_v1:              { name, expires_in_days, replaces_key_id?, grace_hours?, allow_duplicate_name? }
 *   revoke_same_name, legacy: { name, expires_at }
 * The response's `revoked_key_ids` / `retiring` are the only source of truth
 * for what happened to other keys.
 */
export const createApiKey = (body) =>
  send(
    () =>
      apiClient.post(API_ENDPOINTS.API_KEYS, body, {
        timeout: API_KEY_MUTATION_TIMEOUT_MS
      }),
    "Failed to create API key",
    { mutation: true }
  );

/**
 * Revoke an API key immediately.
 * Calls: POST /api/v1/api-keys/{id}/revoke  body { reason? }
 */
export const revokeApiKey = (keyId, reason) => {
  const trimmed = typeof reason === "string" ? reason.trim() : "";
  return send(
    () =>
      apiClient.post(
        `${API_ENDPOINTS.API_KEYS}/${encodeURIComponent(keyId)}/revoke`,
        trimmed ? { reason: trimmed } : {},
        { timeout: API_KEY_MUTATION_TIMEOUT_MS }
      ),
    "Failed to revoke API key",
    { mutation: true }
  );
};
